import { BarChart3, FileText, Table2, Workflow, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  type DateFilter,
  type FilterClearingInfo,
  type FilterColumn,
  type FilterValue,
  formatFilterValue,
  type NumberFilter,
  type SelectFilter,
  type TextFilter,
} from "@/lib/filter-display-utils";

// Re-export filter interfaces for backward compatibility
export type {
  DateFilter,
  NumberFilter,
  TextFilter,
  SelectFilter,
  FilterValue,
  FilterColumn,
};

export type FilterType = "table" | "metric" | "relation" | "report";

interface ActiveFiltersDisplayProps {
  filters: Record<string, unknown>;
  columns: FilterColumn[];
  clearFilter: (key: string, clearingInfo?: FilterClearingInfo) => void;
  clearAllFilters: () => void;
  readOnly?: boolean; // Hide clear buttons when true
  clearAllText?: string; // Custom text for the Clear All button
  filterType?: FilterType; // Type of filter to display appropriate icon
}

export function ActiveFiltersDisplay({
  filters,
  columns,
  clearFilter,
  clearAllFilters,
  readOnly = false,
  clearAllText = "Clear All",
  filterType,
}: ActiveFiltersDisplayProps) {
  if (Object.keys(filters).length === 0) return null;

  // Get the appropriate icon and tooltip based on filter type
  const getFilterTypeIcon = () => {
    switch (filterType) {
      case "table":
        return {
          icon: Table2,
          tooltip: "Table filter: Applied from the column menu in the table",
        };
      case "metric":
        return {
          icon: BarChart3,
          tooltip: "Metric filter: Applied from the Metric Filters modal",
        };
      case "relation":
        return {
          icon: Workflow,
          tooltip: "Relation filter: Applied from the Relation Filters modal",
        };
      case "report":
        return {
          icon: FileText,
          tooltip:
            "Report filter: Set when the report was created and cannot be edited",
        };
      default:
        return null;
    }
  };

  const filterTypeInfo = getFilterTypeIcon();

  return (
    <div className="px-0 py-0">
      <div className="flex flex-wrap gap-2">
        {Object.entries(filters).map(([key, value]) => {
          const column = columns.find((col) => col.key === key);
          if (!column) return null;

          // Use the unified formatting function
          const filterLabel = formatFilterValue(
            value as FilterValue,
            column.filterType,
            undefined, // No field definitions needed for non-structured filters
          );

          // Skip filters with empty labels
          if (!filterLabel) return null;

          // Show loading skeleton for filters waiting for field definitions
          if (filterLabel === "Loading...") {
            return (
              <div key={key} className="flex items-center gap-1">
                <Skeleton className="h-6 w-32 rounded-md" />
              </div>
            );
          }

          return (
            <div
              key={key}
              className="flex items-stretch bg-muted rounded-md text-xs overflow-hidden"
            >
              {filterTypeInfo && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="bg-muted-foreground/10 px-2 flex items-center hover:bg-muted-foreground/15 transition-colors">
                      <filterTypeInfo.icon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="text-xs">{filterTypeInfo.tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              )}
              <div className="flex items-center gap-1.5 px-2 py-1 flex-1">
                <span>
                  {column.title}: {filterLabel}
                </span>
                {!readOnly && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4 p-0 hover:bg-transparent"
                    onClick={() => clearFilter(key, column.clearingInfo)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}

        {Object.keys(filters).length > 0 && !readOnly && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs"
            onClick={clearAllFilters}
          >
            {clearAllText}
          </Button>
        )}
      </div>
    </div>
  );
}
